import React, { useState, useEffect } from 'react';

import apiService from '../services/api';
import Layout from '../components/Layout';
import './BlogAdmin.css';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  content_type: 'markdown' | 'html';
  featured_image_url?: string;
  featured_image_alt?: string;
  meta_title?: string;
  meta_description?: string;
  meta_keywords?: string;
  og_title?: string;
  og_description?: string;
  og_image_url?: string;
  status: 'draft' | 'published' | 'archived';
  published_at?: string;
  category_id?: string;
  author_id: string;
  created_at: string;
  updated_at: string;
}

interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  color: string;
  icon?: string;
  sort_order: number;
}

interface BlogTag {
  id: string;
  name: string;
  slug: string;
  description?: string;
  post_count: number;
}



const BlogAdmin: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'posts' | 'categories' | 'tags'>('posts');
  const [environment, setEnvironment] = useState<'development' | 'staging' | 'production'>('development');
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [tags, setTags] = useState<BlogTag[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Post form state
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [showPostForm, setShowPostForm] = useState(false);
  const [postForm, setPostForm] = useState({
    title: '',
    excerpt: '',
    content: '',
    content_type: 'html' as 'markdown' | 'html',
    featured_image_url: '',
    featured_image_alt: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_title: '',
    og_description: '',
    og_image_url: '',
    status: 'draft' as 'draft' | 'published' | 'archived',
    category_id: '',
    tag_ids: [] as string[]
  });
  
  // Category form state
  const [editingCategory, setEditingCategory] = useState<BlogCategory | null>(null);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    color: '#034C3C',
    icon: '',
    sort_order: 0
  });
  
  // Tag form state
  const [editingTag, setEditingTag] = useState<BlogTag | null>(null);
  const [showTagForm, setShowTagForm] = useState(false);
  const [tagForm, setTagForm] = useState({
    name: '',
    description: ''
  });

  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);

  useEffect(() => {
    fetchData();
  }, [environment]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Set the environment in the API service
      apiService.setEnvironment(environment);

      // First test if the server is running
      const healthData = await apiService.healthCheck();
      console.log('Health check:', healthData);

      const [postsData, categoriesData, tagsData] = await Promise.all([
        apiService.getBlogPosts(),
        apiService.getBlogCategories(),
        apiService.getBlogTags()
      ]);

      setPosts(postsData.posts || []);
      setCategories(categoriesData);
      setTags(tagsData);


    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch data';
      setError(errorMessage);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePostFormChange = (field: string, value: any) => {
    setPostForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCategoryFormChange = (field: string, value: any) => {
    setCategoryForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTagFormChange = (field: string, value: any) => {
    setTagForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const uploadImage = async (file: File): Promise<string> => {
    setUploadingImage(true);
    setImageUploadProgress(0);

    try {
      const result = await apiService.uploadBlogImage(file);
      return result.url;

    } catch (err) {
      throw new Error('Image upload failed');
    } finally {
      setUploadingImage(false);
      setImageUploadProgress(0);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const imageUrl = await uploadImage(file);
      handlePostFormChange('featured_image_url', imageUrl);
    } catch (err) {
      alert('Failed to upload image');
    }
  };

  const savePost = async () => {
    try {
      if (editingPost) {
        await apiService.updateBlogPost(editingPost.id, postForm);
      } else {
        await apiService.createBlogPost(postForm);
      }

      await fetchData();
      closePostForm();
      alert(editingPost ? 'Post updated successfully!' : 'Post created successfully!');

    } catch (err) {
      alert('Failed to save post');
    }
  };

  const saveCategory = async () => {
    try {
      if (editingCategory) {
        // TODO: Add update category method to API service
        throw new Error('Update category not implemented yet');
      } else {
        await apiService.createBlogCategory(categoryForm);
      }

      await fetchData();
      closeCategoryForm();
      alert(editingCategory ? 'Category updated successfully!' : 'Category created successfully!');

    } catch (err) {
      alert('Failed to save category');
    }
  };

  const saveTag = async () => {
    try {
      if (editingTag) {
        // TODO: Add update tag method to API service
        throw new Error('Update tag not implemented yet');
      } else {
        await apiService.createBlogTag(tagForm);
      }

      await fetchData();
      closeTagForm();
      alert(editingTag ? 'Tag updated successfully!' : 'Tag created successfully!');

    } catch (err) {
      alert('Failed to save tag');
    }
  };

  const deletePost = async (postId: string) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await apiService.deleteBlogPost(postId);
      await fetchData();
      alert('Post deleted successfully!');

    } catch (err) {
      alert('Failed to delete post');
    }
  };

  const openPostForm = (post?: BlogPost) => {
    if (post) {
      setEditingPost(post);
      setPostForm({
        title: post.title,
        excerpt: post.excerpt,
        content: post.content,
        content_type: post.content_type,
        featured_image_url: post.featured_image_url || '',
        featured_image_alt: post.featured_image_alt || '',
        meta_title: post.meta_title || '',
        meta_description: post.meta_description || '',
        meta_keywords: post.meta_keywords || '',
        og_title: post.og_title || '',
        og_description: post.og_description || '',
        og_image_url: post.og_image_url || '',
        status: post.status,
        category_id: post.category_id || '',
        tag_ids: []
      });
    } else {
      setEditingPost(null);
      setPostForm({
        title: '',
        excerpt: '',
        content: '',
        content_type: 'html',
        featured_image_url: '',
        featured_image_alt: '',
        meta_title: '',
        meta_description: '',
        meta_keywords: '',
        og_title: '',
        og_description: '',
        og_image_url: '',
        status: 'draft',
        category_id: '',
        tag_ids: []
      });
    }
    setShowPostForm(true);
  };

  const closePostForm = () => {
    setShowPostForm(false);
    setEditingPost(null);
  };

  const openCategoryForm = (category?: BlogCategory) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        description: category.description || '',
        color: category.color,
        icon: category.icon || '',
        sort_order: category.sort_order
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({
        name: '',
        description: '',
        color: '#034C3C',
        icon: '',
        sort_order: 0
      });
    }
    setShowCategoryForm(true);
  };

  const closeCategoryForm = () => {
    setShowCategoryForm(false);
    setEditingCategory(null);
  };

  const openTagForm = (tag?: BlogTag) => {
    if (tag) {
      setEditingTag(tag);
      setTagForm({
        name: tag.name,
        description: tag.description || ''
      });
    } else {
      setEditingTag(null);
      setTagForm({
        name: '',
        description: ''
      });
    }
    setShowTagForm(true);
  };

  const closeTagForm = () => {
    setShowTagForm(false);
    setEditingTag(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { color: '#6b7280', bg: '#f3f4f6' },
      published: { color: '#059669', bg: '#d1fae5' },
      archived: { color: '#dc2626', bg: '#fee2e2' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;
    
    return (
      <span 
        className="status-badge"
        style={{ 
          backgroundColor: config.bg, 
          color: config.color 
        }}
      >
        {status}
      </span>
    );
  };

  const getEnvironmentColor = () => {
    switch (environment) {
      case 'development':
        return '#6B7280';
      case 'staging':
        return '#F59E0B';
      case 'production':
        return '#10B981';
      default:
        return '#6B7280';
    }
  };

  const getEnvironmentIcon = () => {
    switch (environment) {
      case 'development':
        return '🖥️';
      case 'staging':
        return '☁️';
      case 'production':
        return '🚀';
      default:
        return '🖥️';
    }
  };

  if (loading) {
    return (
      <div className="blog-admin-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading blog admin...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="blog-admin-page">
        <div className="error-message">
          <h2>🚨 Connection Error</h2>
          <p>{error}</p>
          {error.includes('Cannot connect to PropTrak API') && (
            <div className="server-instructions">
              <h3>To start the servers:</h3>
              <ol>
                <li><strong>Option A - Use Startup Script (Recommended):</strong></li>
                <li>Open a terminal and run: <code>cd aletheia/backend</code></li>
                <li>Start both servers: <code>python start_blog_system.py</code></li>
                <li>Wait for both servers to start</li>
                <li><strong>Option B - Start Manually:</strong></li>
                <li>Terminal 1: <code>cd proptrak/backend && python simple_app.py</code></li>
                <li>Terminal 2: <code>cd aletheia/backend && python app.py</code></li>
                <li>Come back here and click "Retry"</li>
              </ol>
            </div>
          )}
          <button onClick={fetchData} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-admin-page">
      <Layout>
        <div className="blog-admin-content">
        <div className="blog-admin-header">
          <h1>Blog Management</h1>
          <p>Manage blog posts, categories, and tags</p>
          
          {/* Environment Toggle */}
          <div className="environment-toggle">
            <div className="environment-buttons">
              <button 
                className={`env-btn ${environment === 'development' ? 'active' : ''}`}
                onClick={() => setEnvironment('development')}
              >
                🖥️ Development
              </button>
              <button 
                className={`env-btn ${environment === 'staging' ? 'active' : ''}`}
                onClick={() => setEnvironment('staging')}
              >
                ☁️ Staging
              </button>
              <button 
                className={`env-btn ${environment === 'production' ? 'active' : ''}`}
                onClick={() => setEnvironment('production')}
              >
                🚀 Production
              </button>
            </div>
            <div className="environment-indicator" style={{ color: getEnvironmentColor() }}>
              {getEnvironmentIcon()} {environment.charAt(0).toUpperCase() + environment.slice(1)}
            </div>
          </div>
        </div>

        <div className="blog-admin-tabs">
          <button 
            className={`tab ${activeTab === 'posts' ? 'active' : ''}`}
            onClick={() => setActiveTab('posts')}
          >
            Posts ({posts.length})
          </button>
          <button 
            className={`tab ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            Categories ({categories.length})
          </button>
          <button 
            className={`tab ${activeTab === 'tags' ? 'active' : ''}`}
            onClick={() => setActiveTab('tags')}
          >
            Tags ({tags.length})
          </button>
        </div>

        {/* Posts Tab */}
        {activeTab === 'posts' && (
          <div className="tab-content">
            <div className="content-header">
              <h2>Blog Posts</h2>
              <button 
                onClick={() => openPostForm()}
                className="btn-primary"
              >
                + New Post
              </button>
            </div>

            <div className="posts-grid">
              {posts.map(post => (
                <div key={post.id} className="post-card">
                  <div className="post-image">
                    {post.featured_image_url ? (
                      <img src={post.featured_image_url} alt={post.featured_image_alt || post.title} />
                    ) : (
                      <div className="post-placeholder">No Image</div>
                    )}
                  </div>
                  <div className="post-content">
                    <h3>{post.title}</h3>
                    <p>{post.excerpt}</p>
                    <div className="post-meta">
                      <span>{formatDate(post.created_at)}</span>
                      {getStatusBadge(post.status)}
                    </div>
                    <div className="post-actions">
                      <button 
                        onClick={() => openPostForm(post)}
                        className="btn-secondary"
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => deletePost(post.id)}
                        className="btn-danger"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="tab-content">
            <div className="content-header">
              <h2>Categories</h2>
              <button 
                onClick={() => openCategoryForm()}
                className="btn-primary"
              >
                + New Category
              </button>
            </div>

            <div className="categories-grid">
              {categories.map(category => (
                <div key={category.id} className="category-card">
                  <div 
                    className="category-color"
                    style={{ backgroundColor: category.color }}
                  ></div>
                  <div className="category-content">
                    <h3>{category.name}</h3>
                    <p>{category.description}</p>
                    <div className="category-actions">
                      <button 
                        onClick={() => openCategoryForm(category)}
                        className="btn-secondary"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tags Tab */}
        {activeTab === 'tags' && (
          <div className="tab-content">
            <div className="content-header">
              <h2>Tags</h2>
              <button 
                onClick={() => openTagForm()}
                className="btn-primary"
              >
                + New Tag
              </button>
            </div>

            <div className="tags-grid">
              {tags.map(tag => (
                <div key={tag.id} className="tag-card">
                  <div className="tag-content">
                    <h3>{tag.name}</h3>
                    <p>{tag.description}</p>
                    <span className="tag-count">{tag.post_count} posts</span>
                    <div className="tag-actions">
                      <button 
                        onClick={() => openTagForm(tag)}
                        className="btn-secondary"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Post Form Modal */}
        {showPostForm && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h2>{editingPost ? 'Edit Post' : 'New Post'}</h2>
                <button onClick={closePostForm} className="close-btn">&times;</button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={postForm.title}
                    onChange={(e) => handlePostFormChange('title', e.target.value)}
                    placeholder="Enter post title"
                  />
                </div>

                <div className="form-group">
                  <label>Excerpt</label>
                  <textarea
                    value={postForm.excerpt}
                    onChange={(e) => handlePostFormChange('excerpt', e.target.value)}
                    placeholder="Enter post excerpt"
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <label>Content Type</label>
                  <select
                    value={postForm.content_type}
                    onChange={(e) => handlePostFormChange('content_type', e.target.value)}
                  >
                    <option value="html">Rich Text (HTML)</option>
                    <option value="markdown">Markdown</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Content *</label>
                  <textarea
                    value={postForm.content}
                    onChange={(e) => handlePostFormChange('content', e.target.value)}
                    placeholder="Enter post content"
                    rows={15}
                  />
                </div>

                <div className="form-group">
                  <label>Featured Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                  />
                  {uploadingImage && (
                    <div className="upload-progress">
                      <div 
                        className="progress-bar"
                        style={{ width: `${imageUploadProgress}%` }}
                      ></div>
                    </div>
                  )}
                  {postForm.featured_image_url && (
                    <div className="image-preview">
                      <img src={postForm.featured_image_url} alt="Preview" />
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={postForm.category_id}
                    onChange={(e) => handlePostFormChange('category_id', e.target.value)}
                  >
                    <option value="">Select Category</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={postForm.status}
                    onChange={(e) => handlePostFormChange('status', e.target.value)}
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Meta Title</label>
                  <input
                    type="text"
                    value={postForm.meta_title}
                    onChange={(e) => handlePostFormChange('meta_title', e.target.value)}
                    placeholder="SEO meta title"
                  />
                </div>

                <div className="form-group">
                  <label>Meta Description</label>
                  <textarea
                    value={postForm.meta_description}
                    onChange={(e) => handlePostFormChange('meta_description', e.target.value)}
                    placeholder="SEO meta description"
                    rows={3}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button onClick={closePostForm} className="btn-secondary">
                  Cancel
                </button>
                <button onClick={savePost} className="btn-primary">
                  {editingPost ? 'Update Post' : 'Create Post'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Category Form Modal */}
        {showCategoryForm && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h2>{editingCategory ? 'Edit Category' : 'New Category'}</h2>
                <button onClick={closeCategoryForm} className="close-btn">&times;</button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={categoryForm.name}
                    onChange={(e) => handleCategoryFormChange('name', e.target.value)}
                    placeholder="Enter category name"
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={categoryForm.description}
                    onChange={(e) => handleCategoryFormChange('description', e.target.value)}
                    placeholder="Enter category description"
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <label>Color</label>
                  <input
                    type="color"
                    value={categoryForm.color}
                    onChange={(e) => handleCategoryFormChange('color', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Icon</label>
                  <input
                    type="text"
                    value={categoryForm.icon}
                    onChange={(e) => handleCategoryFormChange('icon', e.target.value)}
                    placeholder="Icon class or emoji"
                  />
                </div>

                <div className="form-group">
                  <label>Sort Order</label>
                  <input
                    type="number"
                    value={categoryForm.sort_order}
                    onChange={(e) => handleCategoryFormChange('sort_order', parseInt(e.target.value))}
                    min="0"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button onClick={closeCategoryForm} className="btn-secondary">
                  Cancel
                </button>
                <button onClick={saveCategory} className="btn-primary">
                  {editingCategory ? 'Update Category' : 'Create Category'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tag Form Modal */}
        {showTagForm && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h2>{editingTag ? 'Edit Tag' : 'New Tag'}</h2>
                <button onClick={closeTagForm} className="close-btn">&times;</button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={tagForm.name}
                    onChange={(e) => handleTagFormChange('name', e.target.value)}
                    placeholder="Enter tag name"
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={tagForm.description}
                    onChange={(e) => handleTagFormChange('description', e.target.value)}
                    placeholder="Enter tag description"
                    rows={3}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button onClick={closeTagForm} className="btn-secondary">
                  Cancel
                </button>
                <button onClick={saveTag} className="btn-primary">
                  {editingTag ? 'Update Tag' : 'Create Tag'}
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </Layout>
    </div>
  );
};

export default BlogAdmin; 